import { alpha, Theme } from '@mui/material/styles'
import { ButtonGroupProps } from '@mui/material/ButtonGroup'

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const

/** Matches the Button override's pill; a large value is clamped to half the height. */
const PILL_RADIUS = 999

export default function ButtonGroup(theme: Theme) {
  const rootStyle = (ownerState: ButtonGroupProps) => {
    const inheritColor = ownerState.color === 'inherit'

    const containedVariant = ownerState.variant === 'contained'

    const outlinedVariant = ownerState.variant === 'outlined'

    const textVariant = ownerState.variant === 'text'

    const softVariant = ownerState.variant === 'soft'

    const horizontalOrientation = ownerState.orientation === 'horizontal'

    const verticalOrientation = ownerState.orientation === 'vertical'

    const defaultStyle = {
      /**
       * A split button is still one control, so the group carries the pill and
       * only its outer corners are rounded — the divider between the segments
       * stays straight. Button.ts's own pill rule is a plain, low-specificity
       * `borderRadius: 999` (so a lone button's sx can opt out), which means a
       * grouped button would inherit that pill on itself unless something
       * squares it back off first — the `.MuiButtonGroup-grouped` rule below
       * does that with higher specificity, then the first/last-of-type rules
       * re-round only the group's outer corners.
       */
      borderRadius: PILL_RADIUS,
      ...(horizontalOrientation && {
        '& .MuiButtonGroup-grouped:first-of-type': {
          borderTopLeftRadius: PILL_RADIUS,
          borderBottomLeftRadius: PILL_RADIUS,
        },
        '& .MuiButtonGroup-grouped:last-of-type': {
          borderTopRightRadius: PILL_RADIUS,
          borderBottomRightRadius: PILL_RADIUS,
        },
      }),
      ...(verticalOrientation && {
        '& .MuiButtonGroup-grouped:first-of-type': {
          borderTopLeftRadius: PILL_RADIUS,
          borderTopRightRadius: PILL_RADIUS,
        },
        '& .MuiButtonGroup-grouped:last-of-type': {
          borderBottomLeftRadius: PILL_RADIUS,
          borderBottomRightRadius: PILL_RADIUS,
        },
      }),
      '& .MuiButtonGroup-grouped': {
        // Square every grouped button first — it would otherwise render its own
        // pill via the Button override — then the first/last-of-type rules
        // above re-round only the two outer corners.
        borderRadius: 0,
        '&:not(:last-of-type)': {
          ...(!outlinedVariant && {
            borderStyle: 'solid',
            ...(inheritColor && {
              borderColor: alpha(theme.palette.grey[500], 0.32),
            }),
            // HORIZONTAL
            ...(horizontalOrientation && {
              borderWidth: '0px 1px 0px 0px',
            }),
            // VERTICAL
            ...(verticalOrientation && {
              borderWidth: '0px 0px 1px 0px',
            }),
          }),
        },
      },
    }

    const colorStyle = COLORS.map(color => ({
      '& .MuiButtonGroup-grouped': {
        '&:not(:last-of-type)': {
          ...(!outlinedVariant && {
            ...(ownerState.color === color && {
              // CONTAINED
              ...(containedVariant && {
                borderColor: alpha(theme.palette[color].dark, 0.48),
              }),
              // TEXT
              ...(textVariant && {
                borderColor: alpha(theme.palette[color].main, 0.48),
              }),
              // SOFT
              ...(softVariant && {
                borderColor: alpha(theme.palette[color].dark, 0.24),
              }),
            }),
          }),
        },
      },
    }))

    const disabledState = {
      '& .MuiButtonGroup-grouped.Mui-disabled': {
        '&:not(:last-of-type)': {
          borderColor: theme.palette.action.disabledBackground,
        },
      },
    }

    return [...colorStyle, defaultStyle, disabledState]
  }

  return {
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonGroupProps }) => rootStyle(ownerState),
      },
    },
  }
}
